/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

/**
 * CodaOffline.js — Stato della sincronizzazione: cosa deve ancora partire e cosa è
 * stato rifiutato.
 *
 * PERCHÉ ESISTE, sezione per sezione:
 *
 * · IN ATTESA — prima c'era solo un badge numerico ("3 operazioni in attesa sync").
 *   In campo, prima di spegnere il telefono o di allontanarsi dalla copertura, un
 *   numero non dice nulla: serve sapere CHE COSA non è partito, per decidere se si
 *   può chiudere o se conviene cercare campo.
 *
 * · NON RIUSCITE — prima un rifiuto definitivo del server cancellava l'operazione e
 *   mostrava "riprova", ma non c'era più niente da riprovare: il contenuto, scritto
 *   da una persona in campo, era perso senza lasciare traccia. Ora resta qui e
 *   l'operatore decide: rimandarlo o scartarlo consapevolmente.
 */
import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, RefreshCw, Trash2, X, Clock, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "./AppCore";
import { listPending, listFailed, retryFailed, discardFailed } from "../lib/offlineQueue";
import confirmDialog from "./confirmDialog";

/** Etichette leggibili: l'operatore non deve dedurre l'azione da un URL REST. */
const ETICHETTE = {
  createSoccorritore:   "Aggiunta soccorritore",
  updateSoccorritore:   "Modifica soccorritore",
  deleteSoccorritore:   "Eliminazione soccorritore",
  validateSoccorritore: "Validazione soccorritore",
  markPartenza:         "Registrazione partenza",
  markRientro:          "Registrazione rientro",
  addTurnoPresenza:     "Aggiunta turno di presenza",
  createSquadra:        "Creazione squadra",
  updateSquadra:        "Modifica squadra",
  deleteSquadra:        "Eliminazione squadra",
  createMissione:       "Creazione missione",
  patchMissione:        "Modifica missione",
  deleteMissione:       "Eliminazione missione",
  updateMissioneStato:  "Cambio stato missione",
  ricercaNegativa:      "Ricerca negativa POA",
  createComunicazione:  "Nuova comunicazione",
  patchComunicazione:   "Modifica comunicazione",
  deleteComunicazione:  "Eliminazione comunicazione",
  putSchedaDisperso:    "Salvataggio Scheda Disperso",
  importZoneGm:         "Import zone da Global Mapper",
  cambioOperatoreTurno: "Cambio turno operatore",
};

/** Risorsa dedotta dall'URL, per le op rimesse in coda dall'interceptor di rete
 *  (opType `replay:<METODO>`): lì il tipo di azione non è noto a priori. */
const RISORSE = {
  soccorritori: "soccorritore", squadre: "squadra", missioni: "missione",
  comunicazioni: "comunicazione", dispositivi: "dispositivo", turni: "turno",
  operazioni: "operazione", documenti: "documento",
};

const etichetta = (op) => {
  if (ETICHETTE[op.opType]) return ETICHETTE[op.opType];
  if (String(op.opType || "").startsWith("replay:")) {
    const metodo = op.opType.split(":")[1];
    const verbo = { POST: "Creazione", PUT: "Modifica", PATCH: "Modifica", DELETE: "Eliminazione" }[metodo] || "Azione";
    const seg = String(op.url || "").split("/").filter(Boolean);
    const risorsa = seg.map((s) => RISORSE[s]).find(Boolean);
    return risorsa ? `${verbo} ${risorsa}` : `${verbo} (interrotta dalla rete)`;
  }
  return op.opType || "Operazione";
};

/** Data/ora dell'AZIONE (quando è stata compiuta), non del sync o del rifiuto. */
const quando = (ms) => {
  if (!ms) return "—";
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, "0");
  const oggi = new Date().toDateString() === d.toDateString();
  const ora = `${p(d.getHours())}:${p(d.getMinutes())}`;
  return oggi ? ora : `${p(d.getDate())}/${p(d.getMonth() + 1)} ${ora}`;
};

/** Estrae dal payload qualche campo riconoscibile, per far capire DI COSA si tratta. */
const dettaglio = (op) => {
  const b = op.body || {};
  const campi = [b.cognome_nome, b.nome, b.sigla, b.oggetto, b.testo, b.descrizione, b.stato];
  const primo = campi.find((v) => typeof v === "string" && v.trim());
  if (primo) return primo.slice(0, 60);
  // Il cambio stato porta l'informazione nei query param, non nel body.
  const st = op.params && op.params.stato;
  return typeof st === "string" ? st.replace(/_/g, " ") : null;
};

const Riga = ({ op, children, tono }) => (
  <div className={`bg-slate-900 border rounded p-3 ${tono === 'rosso' ? 'border-red-800/50' : 'border-slate-700'}`}>
    <div className="flex justify-between items-start gap-3">
      <div className="min-w-0">
        <div className="font-medium text-white text-sm">{etichetta(op)}</div>
        {dettaglio(op) && <div className="text-xs text-slate-400 truncate">{dettaglio(op)}</div>}
        <div className="text-xs text-slate-500 mt-1">
          Azione delle {quando(op.timestamp)}
          {op.acting && <> · in turno: {op.acting}</>}
        </div>
        {op.motivo && <div className="text-xs text-amber-400/90 mt-1">{op.motivo}</div>}
        {op.error && <div className="text-xs text-slate-500 mt-0.5 break-words">{String(op.error).slice(0, 160)}</div>}
      </div>
      {children && <div className="flex gap-1 flex-shrink-0">{children}</div>}
    </div>
  </div>
);

const CodaOffline = ({ isOpen, onClose, onChange, onSync, isOffline }) => {
  const [pending, setPending] = useState([]);
  const [failed, setFailed] = useState([]);
  const [busy, setBusy] = useState(false);

  const carica = useCallback(async () => {
    const [p, f] = await Promise.all([listPending(), listFailed()]);
    setPending(p);
    setFailed(f);
  }, []);

  useEffect(() => { if (isOpen) carica(); }, [isOpen, carica]);

  const handleRetry = async (row) => {
    setBusy(true);
    try {
      await retryFailed(row.id);
      toast.success("Rimessa in coda — verrà inviata al prossimo collegamento");
      await carica();
      onChange && onChange();
    } finally { setBusy(false); }
  };

  const handleDiscard = async (row) => {
    const ok = await confirmDialog({
      title: "Scartare l'operazione?",
      danger: true,
      confirmLabel: "Scarta",
      message: `«${etichetta(row)}»${dettaglio(row) ? ` — ${dettaglio(row)}` : ""}\n\nIl contenuto verrà eliminato definitivamente e non finirà mai sul server.`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await discardFailed(row.id);
      await carica();
      onChange && onChange();
    } finally { setBusy(false); }
  };

  const handleSyncNow = async () => {
    setBusy(true);
    try {
      await onSync();
      await carica();
      onChange && onChange();
    } finally { setBusy(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Sincronizzazione">
      <div className="modal-body">
        {/* ── In attesa ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold">
            <Clock size={15} /> In attesa di sincronizzazione ({pending.length})
          </div>
          {pending.length > 0 && !isOffline && (
            <button className="btn btn-secondary btn-sm" onClick={handleSyncNow} disabled={busy}>
              <CloudUpload size={14} /> Sincronizza ora
            </button>
          )}
        </div>

        {pending.length === 0 ? (
          <p className="text-slate-500 text-sm mb-5">Tutto sincronizzato: niente in sospeso.</p>
        ) : (
          <>
            <p className="text-xs text-slate-400 mb-2">
              {isOffline
                ? "Partiranno da sole appena il dispositivo torna in copertura. Puoi spegnere l'app: la coda è salvata sul dispositivo."
                : "In invio. Se restano qui, tocca «Sincronizza ora»."}
            </p>
            <div className="space-y-2 mb-5">
              {pending.map((op) => <Riga key={`p${op.id}`} op={op} />)}
            </div>
          </>
        )}

        {/* ── Non riuscite ──────────────────────────────────────────── */}
        {failed.length > 0 && (
          <>
            <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-2 pt-3 border-t border-slate-800">
              <AlertTriangle size={15} /> Non riuscite ({failed.length})
            </div>
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 mb-3 text-xs text-slate-300">
              Rifiutate dal server e <strong className="text-amber-300">non più ritentate da sole</strong>.
              Il contenuto è conservato: puoi rimandarle o scartarle. Finché restano
              qui, <strong className="text-amber-300">non sono sul server</strong>.
            </div>
            <div className="space-y-2">
              {failed.map((op) => (
                <Riga key={`f${op.id}`} op={op} tono="rosso">
                  <button className="action-btn edit" title="Rimetti in coda" disabled={busy} onClick={() => handleRetry(op)}>
                    <RefreshCw size={14} />
                  </button>
                  <button className="action-btn delete" title="Scarta definitivamente" disabled={busy} onClick={() => handleDiscard(op)}>
                    <Trash2 size={14} />
                  </button>
                </Riga>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}><X size={14} /> Chiudi</button>
      </div>
    </Modal>
  );
};

export default CodaOffline;
